/**
 * Admin-subtree code-split seam (023-admin-identity, FR-018).
 *
 * `AdminPortal`, `AdminFeedback`, `AdminAnalytics`, and `AdminCmsPage` are exported here as `React.lazy()`
 * references backed by a dynamic `import()`, rather than the eager
 * top-of-file imports the rest of `shell/routes.ts` uses for its
 * components. The dynamic `import()` is the code-split boundary: Vite +
 * Rollup emit the admin route subtree (`/admin`, `/admin/cms/*`, and
 * everything they statically reach: the launch portal + its tiles, the
 * CMS editor, its TipTap chunk, the admin tRPC client) as separate
 * chunk(s) that are NOT in the member entry graph. A member session
 * therefore downloads zero bytes of admin-only code (toward SC-005); the
 * entry chunk carries only the tiny lazy reference until a user actually
 * navigates to an admin path.
 *
 * The `/admin` descriptor was repurposed from the retired demo-denial
 * surface to the `AdminPortal` launch portal (025-admin-area US1, T010);
 * the member-on-admin denial moved to the eager `/forbidden` surface
 * (`AdminForbidden`, member bundle) in the foundational phase. The portal
 * stays lazy here so it remains admin-only-bundle: members never pull it.
 *
 * Mirrors the established `primitives/RichTextEditor.tsx` lazy pattern.
 * `RouteRegistry` supplies the required `<Suspense>` boundary around
 * every rendered route element, so eager routes never show a fallback
 * (they never suspend) and the admin routes load gracefully behind it.
 *
 * These route components are *named* exports, so each dynamic import is
 * adapted to the `{ default }` shape `React.lazy` expects. Reverting
 * any of them to an eager
 * `import { AdminCmsPage } from '../routes/AdminCmsPage.js'` would fold
 * the admin code back into the member entry chunk and defeat the split;
 * `shell/__tests__/routes.lazy.test.tsx` pins this seam so such a
 * regression trips in the unit suite before the byte-level build
 * inspection (T047) runs.
 *
 * Why a dedicated module rather than inlining these `lazy()` calls
 * in `routes.ts`: `routes.ts` is the data-only route table held under
 * the 400-line architecture ceiling (FR-045). Co-locating the seam
 * rationale here keeps that table focused on descriptors and gives the
 * code-split contract a single documented home.
 */

import type { ComponentType } from 'react';
import { type LazyExoticComponent, lazy } from 'react';

export const AdminPortalLazy: LazyExoticComponent<ComponentType> = lazy(() =>
  import('../routes/AdminPortal.js').then((m) => ({ default: m.AdminPortal })),
);

// `/admin/feedback` — the palautteet landing surface (025-admin-area US2,
// T013). Lazy for the same reason as the portal and the CMS shell: it must
// stay in the admin-only bundle so a member downloads zero bytes of it
// (FR-018, toward SC-005). The stub body US5 replaces (its dashboard +
// server-side export) lands behind this same seam without a routes.ts edit.
export const AdminFeedbackLazy: LazyExoticComponent<ComponentType> = lazy(() =>
  import('../routes/AdminFeedback.js').then((m) => ({ default: m.AdminFeedback })),
);

export const AdminAnalyticsLazy: LazyExoticComponent<ComponentType> = lazy(() =>
  import('../routes/AdminAnalytics.js').then((m) => ({ default: m.AdminAnalytics })),
);

export const AdminCmsPageLazy: LazyExoticComponent<ComponentType> = lazy(() =>
  import('../routes/AdminCmsPage.js').then((m) => ({ default: m.AdminCmsPage })),
);
